// AGC: Pattern Matching Admin - CRUD interface for voice command patterns
// AGC: View, create, edit, delete, duplicate patterns

import React, { useState, useEffect, useCallback } from 'react'
import { getBackendUrl } from '../../utils/backendUrl'

// AGC: Pattern type definition
interface VoicePattern {
  id: string
  entryType: string
  priority: number
  patterns: string[]
  confidence: number
  description?: string
  examples?: string[]
  createdAt?: string
  updatedAt?: string
}

// AGC: Entry type with sub-entries structure
interface EntryTypeConfig {
  value: string
  label: string
  color: string
  icon: string
  subEntries?: SubEntryConfig[]
}

interface SubEntryConfig {
  value: string
  label: string
  description: string
}

// AGC: Main entry types with comprehensive sub-entries for each category
const ENTRY_TYPES: EntryTypeConfig[] = [
  { 
    value: 'expense', 
    label: '💰 Expense (Despesa)', 
    color: 'bg-red-100 text-red-800',
    icon: '💰',
    subEntries: [
      { value: 'expense:fuel', label: '⛽ Combustível', description: 'Diesel, gasolina, etanol' },
      { value: 'expense:seeds', label: '🌱 Sementes', description: 'Compra de sementes' },
      { value: 'expense:fertilizer', label: '🧪 Adubo/Fertilizante', description: 'NPK, ureia, calcário' },
      { value: 'expense:pesticides', label: '🧴 Defensivos/Agrotóxicos', description: 'Herbicidas, fungicidas, inseticidas' },
      { value: 'expense:labor', label: '👷 Mão de Obra', description: 'Salários, diárias, terceirizados' },
      { value: 'expense:equipment', label: '🔧 Equipamentos', description: 'Compra de máquinas e implementos' },
      { value: 'expense:maintenance', label: '🔩 Manutenção', description: 'Peças, reparos, serviços técnicos' },
      { value: 'expense:rent', label: '🏠 Aluguel', description: 'Arrendamento de terras, galpões' },
      { value: 'expense:utilities', label: '💡 Utilidades', description: 'Energia elétrica, água, telefone' },
      { value: 'expense:feed', label: '🌾 Ração', description: 'Alimentação animal' },
      { value: 'expense:veterinary', label: '🏥 Veterinário', description: 'Vacinas, medicamentos, consultas' },
      { value: 'expense:transport', label: '🚚 Transporte', description: 'Frete, logística, carretos' },
      { value: 'expense:services', label: '🛠️ Serviços', description: 'Consultoria, análises de solo' },
      { value: 'expense:taxes', label: '📋 Impostos/Taxas', description: 'ITR, FUNRURAL, taxas' },
      { value: 'expense:insurance', label: '🛡️ Seguros', description: 'Seguro agrícola, veículos' },
      { value: 'expense:other', label: '📝 Outros', description: 'Outras despesas não categorizadas' },
    ]
  },
  { 
    value: 'field', 
    label: '🌾 Field (Campo/Talhão)', 
    color: 'bg-green-100 text-green-800',
    icon: '🌾',
    subEntries: [
      { value: 'field:planting', label: '🌱 Plantio', description: 'Registro de plantio' },
      { value: 'field:harvest', label: '🌾 Colheita', description: 'Registro de colheita' },
      { value: 'field:soil_prep', label: '🚜 Preparo do Solo', description: 'Aração, gradagem, subsolagem' },
      { value: 'field:irrigation', label: '💧 Irrigação', description: 'Eventos de irrigação' },
      { value: 'field:spraying', label: '🚿 Pulverização', description: 'Aplicação de defensivos' },
      { value: 'field:fertilization', label: '🧪 Adubação', description: 'Aplicação de fertilizantes' },
      { value: 'field:crop_rotation', label: '🔄 Rotação de Cultura', description: 'Mudança de cultura' },
      { value: 'field:observation', label: '👁️ Observação', description: 'Inspeção visual do campo' },
      { value: 'field:weather_event', label: '⛈️ Evento Climático', description: 'Chuva, geada, granizo, seca' },
      { value: 'field:pest_disease', label: '🐛 Pragas/Doenças', description: 'Ocorrência de pragas ou doenças' },
      { value: 'field:soil_analysis', label: '📊 Análise de Solo', description: 'Coleta e resultado de análise' },
      { value: 'field:boundary', label: '📍 Demarcação', description: 'Definição de limites/GPS' },
      { value: 'field:other', label: '📝 Outros', description: 'Outros eventos de campo' },
    ]
  },
  { 
    value: 'task', 
    label: '📋 Task (Tarefa)', 
    color: 'bg-blue-100 text-blue-800',
    icon: '📋',
    subEntries: [
      { value: 'task:irrigation', label: '💧 Irrigação', description: 'Tarefas de irrigação' },
      { value: 'task:spraying', label: '🚿 Pulverização', description: 'Aplicar defensivos' },
      { value: 'task:fertilization', label: '🧪 Adubação', description: 'Aplicar fertilizantes' },
      { value: 'task:planting', label: '🌱 Plantio', description: 'Tarefas de plantio' },
      { value: 'task:harvest', label: '🌾 Colheita', description: 'Tarefas de colheita' },
      { value: 'task:maintenance', label: '🔧 Manutenção', description: 'Manutenção preventiva' },
      { value: 'task:repair', label: '🔩 Reparo', description: 'Reparos e consertos' },
      { value: 'task:inspection', label: '🔍 Inspeção/Vistoria', description: 'Verificação de equipamentos/áreas' },
      { value: 'task:monitoring', label: '📊 Monitoramento', description: 'Acompanhamento de culturas' },
      { value: 'task:cleaning', label: '🧹 Limpeza', description: 'Limpeza de áreas ou equipamentos' },
      { value: 'task:scheduling', label: '📅 Agendamento', description: 'Programar atividades futuras' },
      { value: 'task:reminder', label: '⏰ Lembrete', description: 'Lembretes gerais' },
      { value: 'task:urgent', label: '🚨 Urgente', description: 'Tarefas urgentes/emergência' },
      { value: 'task:report', label: '📄 Relatório', description: 'Geração de relatórios' },
      { value: 'task:other', label: '📝 Outros', description: 'Outras tarefas' },
    ]
  },
  { 
    value: 'inventory', 
    label: '📦 Inventory (Estoque)', 
    color: 'bg-yellow-100 text-yellow-800',
    icon: '📦',
    subEntries: [
      { value: 'inventory:purchase', label: '🛒 Compra/Entrada', description: 'Entrada de produtos no estoque' },
      { value: 'inventory:consumption', label: '📤 Consumo/Saída', description: 'Uso/saída de produtos' },
      { value: 'inventory:transfer', label: '🔄 Transferência', description: 'Movimentação entre locais' },
      { value: 'inventory:count', label: '📋 Contagem/Inventário', description: 'Conferência de estoque' },
      { value: 'inventory:low_stock', label: '⚠️ Estoque Baixo', description: 'Alerta de reposição' },
      { value: 'inventory:expiration', label: '📅 Vencimento', description: 'Produtos próximos do vencimento' },
      { value: 'inventory:return', label: '↩️ Devolução', description: 'Devolução ao fornecedor' },
      { value: 'inventory:adjustment', label: '⚖️ Ajuste', description: 'Correção de quantidades' },
      { value: 'inventory:reserve', label: '🔒 Reserva', description: 'Reservar para uso futuro' },
      { value: 'inventory:disposal', label: '🗑️ Descarte', description: 'Produtos vencidos/danificados' },
      { value: 'inventory:other', label: '📝 Outros', description: 'Outras operações de estoque' },
    ]
  },
  { 
    value: 'vehicle', 
    label: '🚜 Vehicle (Veículo)', 
    color: 'bg-purple-100 text-purple-800',
    icon: '🚜',
    subEntries: [
      { value: 'vehicle:create', label: '➕ Registrar/Criar', description: 'Cadastrar novo veículo ou máquina' },
      { value: 'vehicle:refuel', label: '⛽ Abastecimento', description: 'Registro de combustível' },
      { value: 'vehicle:maintenance', label: '🔧 Manutenção Preventiva', description: 'Revisões programadas' },
      { value: 'vehicle:repair', label: '🔩 Reparo/Conserto', description: 'Reparos corretivos' },
      { value: 'vehicle:usage_log', label: '📋 Registro de Uso', description: 'Horas/km trabalhados' },
      { value: 'vehicle:tire', label: '🛞 Pneus', description: 'Troca, calibragem, rodízio' },
      { value: 'vehicle:oil_change', label: '🛢️ Troca de Óleo', description: 'Óleo motor, hidráulico' },
      { value: 'vehicle:hours_update', label: '⏱️ Horímetro/Km', description: 'Atualização de hodômetro' },
      { value: 'vehicle:status', label: '📊 Status', description: 'Mudança de disponibilidade' },
      { value: 'vehicle:inspection', label: '🔍 Vistoria', description: 'Inspeção técnica' },
      { value: 'vehicle:cleaning', label: '🧽 Lavagem/Limpeza', description: 'Limpeza do veículo' },
      { value: 'vehicle:parts', label: '⚙️ Peças', description: 'Troca de componentes' },
      { value: 'vehicle:registration', label: '📄 Documentação', description: 'Licenciamento, IPVA' },
      { value: 'vehicle:other', label: '📝 Outros', description: 'Outros eventos de veículo' },
    ]
  },
  { 
    value: 'weather', 
    label: '🌤️ Weather (Clima)', 
    color: 'bg-cyan-100 text-cyan-800',
    icon: '🌤️',
    subEntries: [
      { value: 'weather:rain', label: '🌧️ Chuva', description: 'Registro de precipitação' },
      { value: 'weather:frost', label: '❄️ Geada', description: 'Ocorrência de geada' },
      { value: 'weather:hail', label: '🌨️ Granizo', description: 'Ocorrência de granizo' },
      { value: 'weather:drought', label: '☀️ Seca', description: 'Período de estiagem' },
      { value: 'weather:wind', label: '💨 Vento Forte', description: 'Ventania, vendaval' },
      { value: 'weather:flood', label: '🌊 Alagamento', description: 'Enchente, inundação' },
      { value: 'weather:temperature', label: '🌡️ Temperatura', description: 'Extremos de temperatura' },
      { value: 'weather:humidity', label: '💧 Umidade', description: 'Níveis de umidade' },
      { value: 'weather:other', label: '📝 Outros', description: 'Outros eventos climáticos' },
    ]
  },
  { 
    value: 'worker', 
    label: '👷 Worker (Funcionário)', 
    color: 'bg-orange-100 text-orange-800',
    icon: '👷',
    subEntries: [
      { value: 'worker:attendance', label: '✅ Presença', description: 'Registro de ponto' },
      { value: 'worker:absence', label: '❌ Falta', description: 'Registro de ausência' },
      { value: 'worker:overtime', label: '⏰ Hora Extra', description: 'Horas adicionais' },
      { value: 'worker:assignment', label: '📋 Atribuição', description: 'Designação de tarefas' },
      { value: 'worker:payment', label: '💰 Pagamento', description: 'Salário, diária, adiantamento' },
      { value: 'worker:training', label: '📚 Treinamento', description: 'Capacitação, curso' },
      { value: 'worker:vacation', label: '🏖️ Férias', description: 'Período de férias' },
      { value: 'worker:sick_leave', label: '🏥 Atestado', description: 'Afastamento médico' },
      { value: 'worker:hire', label: '🤝 Contratação', description: 'Novo funcionário' },
      { value: 'worker:termination', label: '📤 Desligamento', description: 'Saída de funcionário' },
      { value: 'worker:other', label: '📝 Outros', description: 'Outros eventos de RH' },
    ]
  },
  { 
    value: 'production', 
    label: '🏭 Production (Produção)', 
    color: 'bg-teal-100 text-teal-800',
    icon: '🏭',
    subEntries: [
      { value: 'production:harvest_record', label: '🌾 Registro Colheita', description: 'Quantidade colhida' },
      { value: 'production:quality', label: '⭐ Qualidade', description: 'Classificação, umidade' },
      { value: 'production:storage', label: '🏪 Armazenagem', description: 'Entrada no silo/armazém' },
      { value: 'production:sale', label: '💵 Venda', description: 'Comercialização' },
      { value: 'production:processing', label: '⚙️ Beneficiamento', description: 'Secagem, limpeza' },
      { value: 'production:loss', label: '📉 Perda', description: 'Perdas na produção' },
      { value: 'production:yield', label: '📊 Produtividade', description: 'Cálculo de rendimento' },
      { value: 'production:other', label: '📝 Outros', description: 'Outros eventos de produção' },
    ]
  },
]

// AGC: Flatten all sub-entries for dropdown selection
const getAllEntryTypes = (): { value: string; label: string; color: string; parentLabel?: string }[] => {
  const allTypes: { value: string; label: string; color: string; parentLabel?: string }[] = []
  
  // AGC: Add main types first
  ENTRY_TYPES.forEach(type => {
    allTypes.push({
      value: type.value,
      label: `${type.icon} ${type.label.replace(/^[^\s]+\s/, '')}`, // AGC: Remove emoji from label
      color: type.color,
    })
    
    // AGC: Add sub-entries
    if (type.subEntries) {
      type.subEntries.forEach(sub => {
        allTypes.push({
          value: sub.value,
          label: sub.label,
          color: type.color,
          parentLabel: type.label,
        })
      })
    }
  })
  
  return allTypes
}

// AGC: Get only main entry types (for backwards compatibility)
const getMainEntryTypes = () => ENTRY_TYPES.map(t => ({ value: t.value, label: t.label, color: t.color }))

// AGC: API base URL - uses environment-aware helper (dev/prod/Capacitor)
const API_BASE = getBackendUrl()

/**
 * AGC: PatternMatchingAdmin - Main component for pattern management
 */
export const PatternMatchingAdmin: React.FC = () => {
  // AGC: State management
  const [patterns, setPatterns] = useState<VoicePattern[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  
  // AGC: Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingPattern, setEditingPattern] = useState<VoicePattern | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // AGC: Test modal state
  const [isTestModalOpen, setIsTestModalOpen] = useState(false)
  const [testInput, setTestInput] = useState('')
  const [testResult, setTestResult] = useState<any>(null)
  const [isTesting, setIsTesting] = useState(false)
  
  // AGC: Sync state
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)

  // AGC: Fetch patterns from API
  const fetchPatterns = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await fetch(`${API_BASE}/api/v1/patterns`)
      
      if (!response.ok) {
        throw new Error(`Failed to fetch patterns: ${response.status}`)
      }
      
      const data = await response.json()
      setPatterns(data.patterns || [])
    } catch (err) {
      console.error('AGC: Error fetching patterns:', err)
      setError(err instanceof Error ? err.message : 'Failed to load patterns')
    } finally {
      setLoading(false)
    }
  }, [])

  // AGC: Load patterns on mount
  useEffect(() => {
    fetchPatterns()
  }, [fetchPatterns])

  /**
   * AGC: Seed default patterns from voiceCommands.js to Firestore
   */
  const handleSeedToFirestore = async () => {
    try {
      setIsSyncing(true)
      setSyncMessage(null)
      setError(null)
      
      const response = await fetch(`${API_BASE}/api/v1/patterns/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      
      if (!response.ok) {
        throw new Error(`Failed to seed patterns: ${response.status}`)
      }
      
      const data = await response.json()
      setSyncMessage(`✅ Seeded ${data.synced || 0} patterns to Firestore!`)
      
      // AGC: Refresh patterns after seeding
      setTimeout(() => {
        fetchPatterns()
        setSyncMessage(null)
      }, 2000)
    } catch (err) {
      console.error('AGC: Error seeding patterns:', err)
      setError(err instanceof Error ? err.message : 'Failed to seed patterns')
    } finally {
      setIsSyncing(false)
    }
  }

  // AGC: Create or update pattern
  const handleSavePattern = async (pattern: VoicePattern) => {
    try {
      setIsSubmitting(true)
      
      const isNew = !pattern.id || pattern.id.startsWith('new-')
      const url = isNew 
        ? `${API_BASE}/api/v1/patterns`
        : `${API_BASE}/api/v1/patterns/${pattern.id}`
      
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pattern),
      })
      
      if (!response.ok) {
        throw new Error(`Failed to save pattern: ${response.status}`)
      }
      
      await fetchPatterns()
      setIsModalOpen(false)
      setEditingPattern(null)
    } catch (err) {
      console.error('AGC: Error saving pattern:', err)
      alert(err instanceof Error ? err.message : 'Failed to save pattern')
    } finally {
      setIsSubmitting(false)
    }
  }

  // AGC: Delete pattern
  const handleDeletePattern = async (patternId: string) => {
    if (!confirm('Are you sure you want to delete this pattern?')) return
    
    try {
      const response = await fetch(`${API_BASE}/api/v1/patterns/${patternId}`, {
        method: 'DELETE',
      })
      
      if (!response.ok) {
        throw new Error(`Failed to delete pattern: ${response.status}`)
      }
      
      await fetchPatterns()
    } catch (err) {
      console.error('AGC: Error deleting pattern:', err)
      alert(err instanceof Error ? err.message : 'Failed to delete pattern')
    }
  }

  // AGC: Duplicate pattern
  const handleDuplicatePattern = (pattern: VoicePattern) => {
    const duplicated: VoicePattern = {
      ...pattern,
      id: `new-${Date.now()}`,
      description: `${pattern.description || pattern.entryType} (Copy)`,
      createdAt: undefined,
      updatedAt: undefined,
    }
    setEditingPattern(duplicated)
    setIsModalOpen(true)
  }

  // AGC: Open edit modal
  const handleEditPattern = (pattern: VoicePattern) => {
    setEditingPattern(pattern)
    setIsModalOpen(true)
  }

  // AGC: Open create modal
  const handleCreatePattern = () => {
    setEditingPattern({
      id: `new-${Date.now()}`,
      entryType: 'expense',
      priority: 5,
      patterns: [''],
      confidence: 0.85,
      description: '',
      examples: [''],
    })
    setIsModalOpen(true)
  }

  // AGC: Test pattern matching
  const handleTestPattern = async () => {
    if (!testInput.trim()) return
    
    try {
      setIsTesting(true)
      setTestResult(null)
      
      const response = await fetch(`${API_BASE}/api/v1/patterns/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: testInput }),
      })
      
      if (!response.ok) {
        throw new Error(`Test failed: ${response.status}`)
      }
      
      const result = await response.json()
      setTestResult(result)
    } catch (err) {
      console.error('AGC: Error testing pattern:', err)
      setTestResult({ error: err instanceof Error ? err.message : 'Test failed' })
    } finally {
      setIsTesting(false)
    }
  }

  // AGC: Filter patterns (supports sub-entry filtering)
  const filteredPatterns = patterns.filter(pattern => {
    const matchesSearch = 
      pattern.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pattern.entryType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pattern.patterns.some(p => p.toLowerCase().includes(searchTerm.toLowerCase()))
    
    // AGC: Enhanced filter logic for sub-entries
    let matchesType = filterType === 'all'
    if (!matchesType) {
      // AGC: Direct match
      if (pattern.entryType === filterType) {
        matchesType = true
      }
      // AGC: Parent type match (e.g., filterType='expense' matches 'expense:fuel')
      else if (pattern.entryType.startsWith(`${filterType}:`)) {
        matchesType = true
      }
      // AGC: Both are sub-entries of same parent
      else if (filterType.includes(':') && pattern.entryType.includes(':')) {
        matchesType = pattern.entryType === filterType
      }
    }
    
    return matchesSearch && matchesType
  })

  // AGC: Get entry type config (supports sub-entries)
  const getEntryTypeConfig = (type: string): { label: string; color: string; icon?: string; subLabel?: string } => {
    // AGC: Check if it's a sub-entry (format: "parent:sub")
    if (type.includes(':')) {
      const [parentType, subType] = type.split(':')
      const parent = ENTRY_TYPES.find(t => t.value === parentType)
      if (parent && parent.subEntries) {
        const subEntry = parent.subEntries.find(s => s.value === type)
        if (subEntry) {
          return {
            label: subEntry.label,
            color: parent.color,
            icon: parent.icon,
            subLabel: subEntry.description,
          }
        }
      }
      // AGC: Fallback for unknown sub-entry
      return {
        label: `${parentType}:${subType}`,
        color: parent?.color || 'bg-gray-100 text-gray-800',
        icon: parent?.icon,
      }
    }
    
    // AGC: Main entry type
    const mainType = ENTRY_TYPES.find(t => t.value === type)
    if (mainType) {
      return {
        label: mainType.label,
        color: mainType.color,
        icon: mainType.icon,
      }
    }
    
    return { label: type, color: 'bg-gray-100 text-gray-800' }
  }

  return (
    <div className="space-y-6">
      {/* AGC: Header with actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200">Voice Command Patterns</h2>
          <p className="text-slate-600 dark:text-slate-300 mt-1">
            Manage regex patterns for voice command recognition
          </p>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={handleSeedToFirestore}
            disabled={isSyncing}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
            title="Upload default patterns from voiceCommands.js to Firestore"
          >
            {isSyncing ? '⏳ Seeding...' : '💾 Seed to Firestore'}
          </button>
          <button
            onClick={() => setIsTestModalOpen(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            🧪 Test Pattern
          </button>
          <button
            onClick={handleCreatePattern}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            ➕ New Pattern
          </button>
        </div>
      </div>

      {/* AGC: Search and filter */}
      <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none p-4 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search patterns..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-admin-bg-dark dark:text-slate-100"
          />
        </div>
        <div>
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            className="w-full sm:w-64 px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-admin-bg-dark dark:text-slate-100"
          >
            <option value="all">📊 All Types</option>
            {ENTRY_TYPES.map(type => (
              <optgroup key={type.value} label={type.label}>
                <option value={type.value}>
                  {type.icon} All {type.label.replace(/^[^\s]+\s/, '').split(' ')[0]}
                </option>
                {type.subEntries?.map(sub => (
                  <option key={sub.value} value={sub.value}>
                    {sub.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <button
          onClick={fetchPatterns}
          className="px-4 py-2 bg-slate-200 dark:bg-white/10 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-colors"
          title="Refresh"
        >
          🔄
        </button>
      </div>

      {/* AGC: Loading state */}
      {loading && (
        <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none p-8 text-center">
          <div className="animate-spin text-4xl mb-4">⏳</div>
          <p className="text-slate-600 dark:text-slate-300">Loading patterns...</p>
        </div>
      )}

      {/* AGC: Success message */}
      {syncMessage && (
        <div className="bg-success-50 dark:bg-emerald-500/15 border border-green-200 dark:border-emerald-500/30 rounded-lg p-4 text-brand-primary dark:text-emerald-400">
          <p className="font-semibold">{syncMessage}</p>
        </div>
      )}

      {/* AGC: Error state */}
      {error && (
        <div className="bg-error-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30 rounded-lg p-4 text-error-700 dark:text-red-400">
          <p className="font-semibold">Error loading patterns</p>
          <p className="text-sm">{error}</p>
          <button
            onClick={fetchPatterns}
            className="mt-2 px-4 py-2 bg-red-100 dark:bg-red-500/20 hover:bg-red-200 dark:hover:bg-red-500/30 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {/* AGC: Patterns list */}
      {!loading && !error && (
        <div className="space-y-4">
          {filteredPatterns.length === 0 ? (
            <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none p-8 text-center text-slate-500 dark:text-slate-400">
              <span className="text-4xl mb-4 block">🔍</span>
              <p>{t('admin.noPatternsFound')}</p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-2 text-green-600 hover:underline"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            filteredPatterns
              .sort((a, b) => b.priority - a.priority)
              .map(pattern => (
                <PatternCard
                  key={pattern.id}
                  pattern={pattern}
                  typeConfig={getEntryTypeConfig(pattern.entryType)}
                  onEdit={() => handleEditPattern(pattern)}
                  onDelete={() => handleDeletePattern(pattern.id)}
                  onDuplicate={() => handleDuplicatePattern(pattern)}
                />
              ))
          )}
        </div>
      )}

      {/* AGC: Stats footer with sub-entry breakdown */}
      {!loading && !error && (
        <div className="bg-slate-50 dark:bg-white/[0.02] rounded-lg p-4 space-y-3">
          <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-300">
            <span className="font-semibold">📊 Total: {patterns.length} patterns</span>
            {ENTRY_TYPES.map(type => {
              // AGC: Count main type + all sub-entries
              const mainCount = patterns.filter(p => p.entryType === type.value).length
              const subCount = patterns.filter(p => p.entryType.startsWith(`${type.value}:`)).length
              const totalCount = mainCount + subCount
              if (totalCount === 0) return null
              return (
                <span key={type.value} className={`px-2 py-0.5 rounded ${type.color}`}>
                  {type.icon} {totalCount}
                </span>
              )
            })}
          </div>
          
          {/* AGC: Sub-entry breakdown (collapsible) */}
          <details className="text-xs text-gray-500">
            <summary className="cursor-pointer hover:text-gray-700">
              📋 View sub-entry breakdown
            </summary>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
              {ENTRY_TYPES.map(type => {
                const subEntryCounts = type.subEntries?.map(sub => ({
                  label: sub.label,
                  count: patterns.filter(p => p.entryType === sub.value).length,
                })).filter(s => s.count > 0)
                
                if (!subEntryCounts || subEntryCounts.length === 0) return null
                
                return (
                  <div key={type.value} className={`p-2 rounded ${type.color} bg-opacity-30`}>
                    <div className="font-semibold text-gray-700 mb-1">{type.icon} {type.value}</div>
                    {subEntryCounts.map(sub => (
                      <div key={sub.label} className="text-gray-600">
                        {sub.label}: {sub.count}
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          </details>
        </div>
      )}

      {/* AGC: Edit/Create Modal */}
      {isModalOpen && editingPattern && (
        <PatternModal
          pattern={editingPattern}
          isSubmitting={isSubmitting}
          onSave={handleSavePattern}
          onClose={() => {
            setIsModalOpen(false)
            setEditingPattern(null)
          }}
        />
      )}

      {/* AGC: Test Modal */}
      {isTestModalOpen && (
        <TestModal
          testInput={testInput}
          testResult={testResult}
          isTesting={isTesting}
          onInputChange={setTestInput}
          onTest={handleTestPattern}
          onClose={() => {
            setIsTestModalOpen(false)
            setTestInput('')
            setTestResult(null)
          }}
        />
      )}
    </div>
  )
}

/**
 * AGC: Pattern Card component
 */
interface PatternCardProps {
  pattern: VoicePattern
  typeConfig: { label: string; color: string; icon?: string; subLabel?: string }
  onEdit: () => void
  onDelete: () => void
  onDuplicate: () => void
}

const PatternCard: React.FC<PatternCardProps> = ({
  pattern,
  typeConfig,
  onEdit,
  onDelete,
  onDuplicate,
}) => {
  const [expanded, setExpanded] = useState(false)
  
  // AGC: Check if this is a sub-entry type
  const isSubEntry = pattern.entryType.includes(':')
  const [parentType] = isSubEntry ? pattern.entryType.split(':') : [pattern.entryType]

  return (
    <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none hover:shadow-md transition-shadow">
      {/* AGC: Card header */}
      <div className="p-4 flex items-start justify-between">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {/* AGC: Show parent type badge for sub-entries */}
            {isSubEntry && (
              <span className={`px-2 py-1 rounded text-xs font-medium ${typeConfig.color} opacity-60`}>
                {parentType.toUpperCase()}
              </span>
            )}
            {/* AGC: Main type/sub-entry badge */}
            <span className={`px-2 py-1 rounded text-xs font-medium ${typeConfig.color}`}>
              {typeConfig.label}
            </span>
            <span className="px-2 py-1 bg-slate-100 dark:bg-white/[0.04] rounded text-xs text-slate-600 dark:text-slate-300">
              Priority: {pattern.priority}
            </span>
            <span className="px-2 py-1 bg-info-50 dark:bg-blue-500/15 rounded text-xs text-info-700 dark:text-blue-400">
              {(pattern.confidence * 100).toFixed(0)}% confidence
            </span>
          </div>

          <h3 className="font-semibold text-slate-800 dark:text-slate-200">
            {pattern.description || `${pattern.entryType} pattern`}
          </h3>

          {/* AGC: Show sub-entry description if available */}
          {typeConfig.subLabel && (
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{typeConfig.subLabel}</p>
          )}

          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {pattern.patterns.length} regex pattern{pattern.patterns.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="flex gap-1">
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 hover:bg-slate-100 dark:hover:bg-white/5 rounded transition-colors"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? '🔼' : '🔽'}
          </button>
          <button
            onClick={onDuplicate}
            className="p-2 hover:bg-slate-100 dark:hover:bg-white/5 rounded transition-colors"
            title="Duplicate"
          >
            📋
          </button>
          <button
            onClick={onEdit}
            className="p-2 hover:bg-info-50 dark:hover:bg-blue-500/15 rounded transition-colors text-blue-600 dark:text-blue-400"
            title="Edit"
          >
            ✏️
          </button>
          <button
            onClick={onDelete}
            className="p-2 hover:bg-error-50 dark:hover:bg-red-500/15 rounded transition-colors text-error-700 dark:text-red-400"
            title="Delete"
          >
            🗑️
          </button>
        </div>
      </div>

      {/* AGC: Expanded content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100 dark:border-white/5 pt-4 space-y-4">
          {/* AGC: Patterns */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Regex Patterns:</h4>
            <div className="space-y-2">
              {pattern.patterns.map((p, idx) => (
                <code
                  key={idx}
                  className="block bg-slate-100 dark:bg-white/[0.04] px-3 py-2 rounded text-sm font-mono text-slate-700 dark:text-slate-300 overflow-x-auto"
                >
                  {p}
                </code>
              ))}
            </div>
          </div>

          {/* AGC: Examples */}
          {pattern.examples && pattern.examples.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Examples:</h4>
              <ul className="list-disc list-inside text-sm text-slate-600 dark:text-slate-300 space-y-1">
                {pattern.examples.map((ex, idx) => (
                  <li key={idx}>&quot;{ex}&quot;</li>
                ))}
              </ul>
            </div>
          )}

          {/* AGC: Metadata */}
          <div className="text-xs text-slate-400 dark:text-slate-500 flex gap-4">
            {pattern.createdAt && (
              <span>Created: {new Date(pattern.createdAt).toLocaleString('pt-BR')}</span>
            )}
            {pattern.updatedAt && (
              <span>Updated: {new Date(pattern.updatedAt).toLocaleString('pt-BR')}</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * AGC: Pattern Edit/Create Modal
 */
interface PatternModalProps {
  pattern: VoicePattern
  isSubmitting: boolean
  onSave: (pattern: VoicePattern) => void
  onClose: () => void
}

const PatternModal: React.FC<PatternModalProps> = ({
  pattern: initialPattern,
  isSubmitting,
  onSave,
  onClose,
}) => {
  const [pattern, setPattern] = useState<VoicePattern>(initialPattern)

  const isNew = !initialPattern.id || initialPattern.id.startsWith('new-')

  // AGC: Update pattern field
  const updateField = <K extends keyof VoicePattern>(field: K, value: VoicePattern[K]) => {
    setPattern(prev => ({ ...prev, [field]: value }))
  }

  // AGC: Add new regex pattern
  const addPattern = () => {
    setPattern(prev => ({
      ...prev,
      patterns: [...prev.patterns, ''],
    }))
  }

  // AGC: Remove regex pattern
  const removePattern = (index: number) => {
    setPattern(prev => ({
      ...prev,
      patterns: prev.patterns.filter((_, i) => i !== index),
    }))
  }

  // AGC: Update regex pattern
  const updatePattern = (index: number, value: string) => {
    setPattern(prev => ({
      ...prev,
      patterns: prev.patterns.map((p, i) => i === index ? value : p),
    }))
  }

  // AGC: Add example
  const addExample = () => {
    setPattern(prev => ({
      ...prev,
      examples: [...(prev.examples || []), ''],
    }))
  }

  // AGC: Remove example
  const removeExample = (index: number) => {
    setPattern(prev => ({
      ...prev,
      examples: (prev.examples || []).filter((_, i) => i !== index),
    }))
  }

  // AGC: Update example
  const updateExample = (index: number, value: string) => {
    setPattern(prev => ({
      ...prev,
      examples: (prev.examples || []).map((e, i) => i === index ? value : e),
    }))
  }

  // AGC: Handle submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // AGC: Validate
    if (!pattern.entryType) {
      alert('Please select an entry type')
      return
    }
    
    if (pattern.patterns.filter(p => p.trim()).length === 0) {
      alert('Please add at least one regex pattern')
      return
    }
    
    // AGC: Clean up empty patterns
    const cleanedPattern = {
      ...pattern,
      patterns: pattern.patterns.filter(p => p.trim()),
      examples: (pattern.examples || []).filter(e => e.trim()),
    }
    
    onSave(cleanedPattern)
  }

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[90] p-4">
      <div className="bg-white dark:bg-admin-card-dark dark:border dark:border-white/5 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* AGC: Modal header */}
        <div className="bg-slate-100 dark:bg-white/[0.04] px-6 py-4 border-b border-slate-200 dark:border-white/5 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200">
            {isNew ? '➕ New Pattern' : '✏️ Edit Pattern'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-200 dark:hover:bg-white/10 rounded transition-colors text-slate-500 dark:text-slate-400"
          >
            ✕
          </button>
        </div>

        {/* AGC: Modal body */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* AGC: Entry Type with Sub-entries */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Entry Type *
              </label>
              <select
                value={pattern.entryType}
                onChange={e => updateField('entryType', e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100"
                required
              >
                {ENTRY_TYPES.map(type => (
                  <optgroup key={type.value} label={type.label}>
                    {/* AGC: Main entry type option */}
                    <option value={type.value}>
                      {type.icon} {type.label.replace(/^[^\s]+\s/, '')} (General)
                    </option>
                    {/* AGC: Sub-entry options */}
                    {type.subEntries?.map(sub => (
                      <option key={sub.value} value={sub.value}>
                        {sub.label} - {sub.description}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Select a specific sub-category for better pattern matching accuracy
              </p>
            </div>

            {/* AGC: Description */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Description
              </label>
              <input
                type="text"
                value={pattern.description || ''}
                onChange={e => updateField('description', e.target.value)}
                placeholder="e.g., 'Expense commands - gastei/paguei patterns'"
                className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100"
              />
            </div>

            {/* AGC: Priority and Confidence */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Priority (1-10)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={pattern.priority}
                  onChange={e => updateField('priority', parseInt(e.target.value) || 5)}
                  className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Higher = checked first</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Confidence (0-1)
                </label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  value={pattern.confidence}
                  onChange={e => updateField('confidence', parseFloat(e.target.value) || 0.85)}
                  className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Match confidence score</p>
              </div>
            </div>

            {/* AGC: Regex Patterns */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Regex Patterns *
              </label>
              <div className="space-y-2">
                {pattern.patterns.map((p, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      type="text"
                      value={p}
                      onChange={e => updatePattern(idx, e.target.value)}
                      placeholder="e.g., gastei\s+(\d+)\s+reais?\s+de\s+(.+)"
                      className="flex-1 px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-green-500 font-mono text-sm dark:bg-admin-bg-dark dark:text-slate-100"
                    />
                    <button
                      type="button"
                      onClick={() => removePattern(idx)}
                      className="px-3 py-2 bg-error-50 dark:bg-red-500/15 hover:bg-red-200 dark:hover:bg-red-500/25 text-error-700 dark:text-red-400 rounded-lg transition-colors"
                      disabled={pattern.patterns.length <= 1}
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addPattern}
                className="mt-2 px-4 py-2 bg-slate-100 dark:bg-white/[0.04] hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-colors text-sm text-slate-700 dark:text-slate-300"
              >
                ➕ Add Pattern
              </button>
              <p className="text-xs text-gray-500 mt-2">
                Use JavaScript regex syntax. Groups () capture data for extraction.
              </p>
            </div>

            {/* AGC: Examples */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Examples (for documentation)
              </label>
              <div className="space-y-2">
                {(pattern.examples || []).map((ex, idx) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      type="text"
                      value={ex}
                      onChange={e => updateExample(idx, e.target.value)}
                      placeholder="e.g., 'Gastei 50 reais de combustível'"
                      className="flex-1 px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-admin-bg-dark dark:text-slate-100"
                    />
                    <button
                      type="button"
                      onClick={() => removeExample(idx)}
                      className="px-3 py-2 bg-error-50 dark:bg-red-500/15 hover:bg-red-200 dark:hover:bg-red-500/25 text-error-700 dark:text-red-400 rounded-lg transition-colors"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addExample}
                className="mt-2 px-4 py-2 bg-slate-100 dark:bg-white/[0.04] hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-colors text-sm text-slate-700 dark:text-slate-300"
              >
                ➕ Add Example
              </button>
            </div>
          </div>
        </form>

        {/* AGC: Modal footer */}
        <div className="bg-slate-100 dark:bg-white/[0.04] px-6 py-4 border-t border-slate-200 dark:border-white/5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 bg-slate-200 dark:bg-white/10 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-brand-primary text-white dark:bg-brand-light dark:text-[#052e22] hover:bg-brand-dark dark:hover:opacity-90 rounded-lg transition-colors flex items-center gap-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin">⏳</span>
                Saving...
              </>
            ) : (
              <>
                💾 Save Pattern
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * AGC: Test Modal
 */
interface TestModalProps {
  testInput: string
  testResult: any
  isTesting: boolean
  onInputChange: (value: string) => void
  onTest: () => void
  onClose: () => void
}

const TestModal: React.FC<TestModalProps> = ({
  testInput,
  testResult,
  isTesting,
  onInputChange,
  onTest,
  onClose,
}) => (
  <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[90] p-4">
    <div className="bg-white dark:bg-admin-card-dark dark:border dark:border-white/5 rounded-lg shadow-xl w-full max-w-lg">
      {/* AGC: Header */}
      <div className="bg-blue-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
        <h2 className="text-xl font-bold">🧪 Test Pattern Matching</h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-blue-500 rounded transition-colors"
        >
          ✕
        </button>
      </div>

      {/* AGC: Body */}
      <div className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
            Test Input (simulated transcription)
          </label>
          <textarea
            value={testInput}
            onChange={e => onInputChange(e.target.value)}
            placeholder="e.g., 'Gastei cinquenta reais de combustível'"
            className="w-full px-4 py-3 border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-blue-500 h-24 dark:bg-admin-bg-dark dark:text-slate-100"
          />
        </div>

        <button
          onClick={onTest}
          className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
          disabled={isTesting || !testInput.trim()}
        >
          {isTesting ? (
            <>
              <span className="animate-spin">⏳</span>
              Testing...
            </>
          ) : (
            <>
              🔍 Test Pattern Match
            </>
          )}
        </button>

        {/* AGC: Result */}
        {testResult && (
          <div className={`p-4 rounded-lg ${testResult.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
            <h4 className="font-semibold mb-2">
              {testResult.error ? '❌ Error' : '✅ Match Result'}
            </h4>
            <pre className="text-sm font-mono overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(testResult, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  </div>
)

export default PatternMatchingAdmin

